import { useEffect, useMemo, useState } from "react";
import { api, unwrap } from "@/lib/api";
import { Megaphone, Send, ChevronDown, ChevronUp, Loader2, Eye, X, Sparkles, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

/**
 * Campaign + segment builder.
 * Builds a filter, previews matches live, composes a WhatsApp message,
 * dispatches through /api/campaigns/{id}/send. All sends log notifications.
 */
export function CampaignBuilder({ branchId }) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState([]);

  const refresh = () => {
    api.get("/campaigns").then((r) => setHistory(unwrap(r) || [])).catch(() => {});
  };

  useEffect(() => { if (open) refresh(); }, [open]);

  return (
    <div className="glass-primary rounded-[20px] overflow-hidden" data-testid="campaign-builder-view">
      <button
        onClick={() => setOpen((x) => !x)}
        data-testid="campaign-builder-toggle"
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/30 transition"
      >
        <div className="flex items-center gap-3">
          <Megaphone size={16} className="text-[var(--color-mauve-deep)]" />
          <div>
            <div className="font-sora font-semibold text-[15px] text-[var(--color-text-primary)]">Campaigns & segments</div>
            <div className="font-inter text-[12px] text-[var(--color-text-muted)]">
              Send WhatsApp to a targeted client segment
            </div>
          </div>
        </div>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {open && (
        <div className="px-5 pb-5 anim-slide-up space-y-4">
          <Composer branchId={branchId} onSent={refresh} />

          {history.length > 0 && (
            <div className="rounded-2xl p-4" style={{ background: "rgba(94,64,96,0.04)", border: "1px solid rgba(94,64,96,0.18)" }}>
              <div className="overline mb-2">Recent campaigns</div>
              <div className="space-y-1.5">
                {history.slice(0, 6).map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-white/55 border border-white/80" data-testid={`campaign-row-${c.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-sora font-semibold text-[13px] truncate">{c.name}</div>
                      <div className="font-inter text-[11.5px] text-[var(--color-text-muted)]">
                        {c.recipients_count} matched · {c.sent_count} sent · {c.status} · {new Date(c.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Composer({ branchId, onSent }) {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("Hi {name} ✨ A special offer awaits at GLOSS — reply to claim!");
  const [segment, setSegment] = useState({
    tags_any: [],
    gender: "all",
    location_contains: "",
    occupation_contains: "",
    min_total_visits: "",
    inactive_days_min: "",
    has_birthday_within_days: "",
    has_anniversary_within_days: "",
    exclude_opt_outs: true,
  });
  const [preview, setPreview] = useState({ count: 0, sample: [] });
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Build server payload from form state (strip empties)
  const payload = useMemo(() => {
    const p = { exclude_opt_outs: segment.exclude_opt_outs, branch_id: branchId || null };
    if (segment.gender && segment.gender !== "all") p.gender = segment.gender;
    if (segment.tags_any.length) p.tags_any = segment.tags_any;
    if (segment.location_contains.trim()) p.location_contains = segment.location_contains.trim();
    if (segment.occupation_contains.trim()) p.occupation_contains = segment.occupation_contains.trim();
    if (segment.min_total_visits !== "" && segment.min_total_visits != null) p.min_total_visits = Number(segment.min_total_visits);
    if (segment.inactive_days_min !== "" && segment.inactive_days_min != null) p.inactive_days_min = Number(segment.inactive_days_min);
    if (segment.has_birthday_within_days !== "" && segment.has_birthday_within_days != null) p.has_birthday_within_days = Number(segment.has_birthday_within_days);
    if (segment.has_anniversary_within_days !== "" && segment.has_anniversary_within_days != null) p.has_anniversary_within_days = Number(segment.has_anniversary_within_days);
    return p;
  }, [segment, branchId]);

  // Auto-preview on every change (debounced)
  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/segments/preview", payload);
        if (!cancelled) setPreview(unwrap(r) || { count: 0, sample: [] });
      } catch {
        if (!cancelled) setPreview({ count: 0, sample: [] });
      } finally {
        if (!cancelled) setBusy(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [payload]);

  const toggleTag = (t) => {
    setSegment((s) => ({
      ...s,
      tags_any: s.tags_any.includes(t) ? s.tags_any.filter((x) => x !== t) : [...s.tags_any, t],
    }));
  };

  const sendNow = async () => {
    if (!name.trim()) { toast.error("Give your campaign a name"); return; }
    if (!message.trim()) { toast.error("Write a message"); return; }
    if (preview.count === 0) { toast.error("Segment is empty — nothing to send"); return; }
    setSending(true);
    try {
      const c = unwrap(await api.post("/campaigns", {
        name: name.trim(), message: message.trim(),
        segment: payload, channel: "whatsapp",
        branch_id: branchId || null,
      }));
      const res = unwrap(await api.post(`/campaigns/${c.id}/send`));
      toast.success(`Campaign sent to ${res.sent} client${res.sent === 1 ? "" : "s"}`, {
        description: res.failed > 0 ? `${res.failed} failed` : "Logged to notifications · WhatsApp dispatched",
      });
      setName(""); onSent?.();
    } catch (e) {
      const data = e?.response?.data?.error;
      toast.error(data?.message || "Could not send campaign", { description: data?.code || "" });
    } finally { setSending(false); }
  };

  const tags = ["VIP", "regular", "new", "bridal", "membership"];

  return (
    <div className="rounded-2xl p-5 space-y-4" style={{ background: "rgba(139,92,246,0.04)", border: "1px solid rgba(139,92,246,0.22)" }}>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-4">
        <div className="space-y-3">
          <label className="block">
            <div className="font-inter text-[12.5px] font-medium text-[var(--color-text-secondary)] mb-1.5">Campaign name</div>
            <input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="May flash sale · VIPs in Koregaon Park"
                   data-testid="campaign-name-input"
                   className={inputCls} />
          </label>

          <label className="block">
            <div className="font-inter text-[12.5px] font-medium text-[var(--color-text-secondary)] mb-1.5">Message · {"{name}"} renders the client's first name</div>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)}
                      rows={3}
                      data-testid="campaign-message-input"
                      className={`${inputCls} h-auto py-2.5 font-inter text-[13px]`} />
          </label>
        </div>

        <div className="rounded-2xl p-4 flex flex-col justify-between" style={{ background: "linear-gradient(180deg,#5E4060 0%,#3F2A45 100%)", color: "white" }} data-testid="campaign-preview-card">
          <div>
            <div className="font-sora font-bold uppercase text-[10.5px] tracking-[0.25em] opacity-80">Will reach</div>
            <div className="font-mono text-[44px] leading-none mt-1" style={{ color: "#F0D898" }} data-testid="campaign-preview-count">
              {busy ? <Loader2 size={28} className="animate-spin" /> : preview.count}
            </div>
            <div className="font-inter text-[12px] opacity-80 mt-1">client{preview.count === 1 ? "" : "s"}</div>
          </div>
          <div className="flex gap-1 mt-3">
            <button onClick={() => setPreviewOpen(true)} className="btn-3d btn-glass-3d btn-sm flex-1" data-testid="campaign-preview-btn">
              <Eye size={12} /> Preview
            </button>
            <button onClick={sendNow} disabled={sending || preview.count === 0} data-testid="campaign-send-btn"
                    className="btn-3d btn-gold-3d btn-sm flex-1">
              {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send
            </button>
          </div>
        </div>
      </div>

      {/* Filter chips */}
      <div className="space-y-3">
        <div>
          <div className="overline mb-1.5">Tags · any match</div>
          <div className="flex flex-wrap gap-1.5">
            {tags.map((t) => {
              const on = segment.tags_any.includes(t);
              return (
                <button key={t} type="button" onClick={() => toggleTag(t)}
                        data-testid={`campaign-tag-${t}`}
                        className={`px-3 py-1.5 rounded-full text-[12px] font-inter font-medium border transition ${on ? "bg-[var(--color-mauve)]/15 border-[var(--color-mauve)]/45 text-[var(--color-mauve-deep)]" : "bg-white/70 border-white/80 text-[var(--color-text-secondary)]"}`}>
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Mini label="Gender">
            <select value={segment.gender} onChange={(e) => setSegment({ ...segment, gender: e.target.value })}
                    data-testid="campaign-gender-select" className={inputCls}>
              <option value="all">All</option>
              <option value="female">Female</option>
              <option value="male">Male</option>
              <option value="other">Other</option>
            </select>
          </Mini>
          <Mini label="Location contains">
            <input value={segment.location_contains}
                   onChange={(e) => setSegment({ ...segment, location_contains: e.target.value })}
                   data-testid="campaign-location-input"
                   placeholder="Koregaon, Aundh…" className={inputCls} />
          </Mini>
          <Mini label="Occupation contains">
            <input value={segment.occupation_contains}
                   onChange={(e) => setSegment({ ...segment, occupation_contains: e.target.value })}
                   placeholder="Doctor, Marketing…" className={inputCls} />
          </Mini>
          <Mini label="Min total visits">
            <input type="number" min="0" value={segment.min_total_visits}
                   onChange={(e) => setSegment({ ...segment, min_total_visits: e.target.value })}
                   data-testid="campaign-min-visits-input"
                   className={`${inputCls} font-mono`} />
          </Mini>
          <Mini label="Inactive ≥ N days">
            <input type="number" min="0" value={segment.inactive_days_min}
                   onChange={(e) => setSegment({ ...segment, inactive_days_min: e.target.value })}
                   placeholder="60, 90…"
                   className={`${inputCls} font-mono`} />
          </Mini>
          <Mini label="Birthday in ≤ N days">
            <input type="number" min="0" value={segment.has_birthday_within_days}
                   onChange={(e) => setSegment({ ...segment, has_birthday_within_days: e.target.value })}
                   placeholder="7, 30…"
                   className={`${inputCls} font-mono`} />
          </Mini>
        </div>
      </div>

      {previewOpen && (
        <PreviewModal
          payload={payload}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}

function PreviewModal({ payload, onClose }) {
  const [data, setData] = useState({ count: 0, sample: [] });
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    api.post("/segments/preview", payload).then((r) => setData(unwrap(r) || { count: 0, sample: [] })).finally(() => setBusy(false));
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [payload]);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center anim-fade-in"
         style={{ background: "rgba(28,25,23,0.45)", backdropFilter: "blur(12px)" }}
         data-testid="campaign-preview-modal">
      <div className="glass-elevated rounded-[24px] p-6 w-[min(540px,calc(100vw-32px))] max-h-[80vh] overflow-auto anim-slide-up-lg space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <div className="overline mb-1">Segment preview</div>
            <h2 className="font-display text-[22px] font-bold tracking-[-0.02em]">{data.count} client{data.count === 1 ? "" : "s"} will receive this</h2>
            <p className="font-inter text-[12px] text-[var(--color-text-muted)] mt-1">First 10 shown</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full glass-subtle flex items-center justify-center"><X size={16} /></button>
        </div>
        {busy ? <div className="font-inter text-[13px] text-[var(--color-text-muted)] py-4">Loading…</div> : (
          <div className="space-y-1.5">
            {data.sample.length === 0 && <div className="font-inter text-[13px] text-[var(--color-text-muted)]">No matches.</div>}
            {data.sample.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-white/60 border border-white/80">
                <div className="min-w-0">
                  <div className="font-sora font-semibold text-[13px]">{c.name}</div>
                  <div className="font-inter text-[11.5px] text-[var(--color-text-muted)]">
                    {c.phone} · {c.total_visits} visits {c.location && ` · ${c.location}`} {c.occupation && ` · ${c.occupation}`}
                  </div>
                </div>
                <div className="flex gap-1">
                  {(c.tags || []).slice(0, 2).map((t) => (
                    <span key={t} className="status-pill" style={{ background: "rgba(94,64,96,0.10)", color: "#5E4060", padding: "2px 8px", fontSize: 10 }}>{t}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Mini({ label, children }) {
  return (
    <label className="block">
      <div className="font-inter text-[11.5px] font-medium text-[var(--color-text-muted)] mb-1">{label}</div>
      {children}
    </label>
  );
}

const inputCls = "w-full h-10 px-3 rounded-xl bg-white/70 border border-white/80 font-inter text-[13.5px] text-[var(--color-text-primary)] outline-none focus:border-[var(--color-mauve)] focus:ring-2 focus:ring-[var(--color-mauve)]/15 transition";

export default CampaignBuilder;
